import { useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { Overwrite } from 'utility-types';

import { SearchInstanceDto } from '@app/components';
import { formatArrayToString, makeRequest } from '@app/utils';

type VirtualShelfField =
  | 'contributors'
  | 'title'
  | 'id'
  | 'isbns'
  | 'sourceTypes';

type VirtualShelfItemDto = Pick<SearchInstanceDto, VirtualShelfField>;

interface VirtualShelfDto {
  instances: VirtualShelfItemDto[];
}

export type VirtualShelfItem = Overwrite<
  VirtualShelfItemDto,
  { sourceTypes?: string }
>;

interface VirtualShelf {
  virtualShelfQuery: UseQueryResult<VirtualShelfItemDto[], null>;
  relatedItems?: VirtualShelfItem[];
}

export const BROWSE_SHELF_INSTANCES_LIMIT = 25;

export const useVirtualShelfFetch = (order?: string): VirtualShelf => {
  const [relatedItems, setRelatedItems] = useState<VirtualShelfItem[]>();

  const virtualShelfQuery = useQuery<
    VirtualShelfDto,
    null,
    VirtualShelfItemDto[]
  >(
    ['instance-details', 'virtual-shelf', order],
    () =>
      makeRequest<VirtualShelfDto>({
        url: '/search/virtual-shelves/instances',
        params: {
          expandAll: true,
          effectiveShelvingOrder: order,
          limit: BROWSE_SHELF_INSTANCES_LIMIT,
          type: 'AROUND',
        },
      }),
    {
      enabled: Boolean(order),
      select: ({ instances }) => instances,
    }
  );

  useEffect(() => {
    if (virtualShelfQuery.isFetched) {
      const instances = virtualShelfQuery.data;

      setRelatedItems(
        instances?.map(({ id, title, contributors, sourceTypes, isbns }) => ({
          id,
          title,
          sourceTypes: formatArrayToString(sourceTypes),
          contributors,
          isbns,
        }))
      );
    }
  }, [virtualShelfQuery.data, virtualShelfQuery.isFetched]);

  return {
    relatedItems,
    virtualShelfQuery,
  };
};
